import { WHITELISTED_DOMAINS_MAX_COUNT } from '@va/constants';
import { useGetWhitelistedDomains } from '@va/dashboard/api-client/Website';
import { useAddNotification, useFormError } from '@va/dashboard/util-hooks';
import { validateWildcardDomain } from '@va/util/helpers';
import { isEqual } from 'lodash';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useUpdateWhitelistedDomains } from '../apiClient';

export default function useDomainWhitelistingCard() {
  const [inputValue, setInputValue] = useState<string>('');
  const { formError, handleFormError } = useFormError();
  const [successSubmit, setSuccessSubmit] = useState(false);
  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const {
    execute: updateDomains,
    isLoading: isUpdating,
    isSucceeded: isUpdateSuccess,
    error: updateError,
  } = useUpdateWhitelistedDomains();

  const { data: currentWhitelistedDomains, isLoading, mutate: mutateWhitelistedDomains } = useGetWhitelistedDomains();
  const [whitelistedDomains, setWhitelistedDomains] = useState<string[]>([]);

  const hasMadeChanges = useMemo(
    () => !isLoading && whitelistedDomains && !isEqual(currentWhitelistedDomains, whitelistedDomains),
    [currentWhitelistedDomains, isLoading, whitelistedDomains],
  );

  useEffect(() => {
    if (currentWhitelistedDomains) {
      setWhitelistedDomains(currentWhitelistedDomains);
    }
  }, [currentWhitelistedDomains]);

  useEffect(() => {
    if (isUpdateSuccess) {
      showSuccessNotification(2000);
      mutateWhitelistedDomains();
    }
    if (updateError) {
      showErrorNotification(2000);
      mutateWhitelistedDomains();
    }
  }, [updateError, isUpdateSuccess, showErrorNotification, showSuccessNotification, mutateWhitelistedDomains]);

  const onChange = (value: string) => setInputValue(value);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validateWildcardDomain(inputValue)) {
      return handleFormError('card.whiteListing.whitelistingDomainValidationMessage');
    }
    if (whitelistedDomains.includes(inputValue)) {
      return handleFormError('card.whiteListing.alreadyAddedDomainMessage');
    }
    if (whitelistedDomains.length > WHITELISTED_DOMAINS_MAX_COUNT) {
      return handleFormError('card.whiteListing.error.maxWhitelistReached');
    }

    setWhitelistedDomains((prev) => [...prev, inputValue]);
    handleSuccessSubmit();
  };

  const handleSuccessSubmit = () => {
    setInputValue('');
    setSuccessSubmit(true);
    setTimeout(() => {
      setSuccessSubmit(false);
    }, 500);
  };

  const onRemove = (value: string) => setWhitelistedDomains((prev) => prev.filter((domain) => domain !== value));

  const onSave = () => {
    if (!whitelistedDomains) return;
    const newAddedDomains = whitelistedDomains.filter((domain) => !currentWhitelistedDomains?.includes(domain));
    const deletedDomains = currentWhitelistedDomains?.filter((domain) => !whitelistedDomains.includes(domain));
    updateDomains(newAddedDomains, deletedDomains);
  };

  return {
    whitelistedDomains,
    inputValue,
    onRemove,
    onSubmit,
    onChange,
    successSubmit,
    error: formError,
    hasMadeChanges,
    onSave,
    isLoading,
    isLoadingUpdateCall: isUpdating,
  };
}
